import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, X, AlertCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';

interface LogoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LogoutModal: React.FC<LogoutModalProps> = ({ isOpen, onClose }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear any remaining local data
      localStorage.clear();
      
      // Close modal and navigate to dashboard
      onClose();
      navigate('/dashboard');
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  // Use portal to render modal at document root
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999]"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className="fixed inset-0 flex items-center justify-center z-[10000] px-4"
            onClick={onClose}
          >
            <div 
              className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative px-8 pt-8 pb-6">
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="absolute top-6 right-6 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" strokeWidth={2} />
                </motion.button>

                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="text-center"
                >
                  {/* Icon with animation */}
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    animate={{
                      rotate: [0, -5, 5, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  >
                    <LogOut className="w-8 h-8 text-red-600" strokeWidth={1.5} />
                  </motion.div>
                  
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    Sign Out?
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Are you sure you want to sign out of your account?
                  </p>
                </motion.div>
              </div>

              {/* Warning Message */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mx-8 mb-6"
              >
                <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 font-medium">
                      Your documents are saved
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      All your work is safely stored in the cloud. You can access it again when you sign back in.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Actions */}
              <div className="px-8 pb-8 space-y-3">
                {/* Logout Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full relative overflow-hidden group"
                >
                  <div className="relative px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center space-x-2 shadow-lg">
                    {isLoggingOut ? (
                      <>
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                        <span className="text-white font-medium">Signing out...</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4 text-white" strokeWidth={2} />
                        <span className="text-white font-medium">Sign Out</span>
                      </>
                    )}
                  </div>
                  
                  {/* Animated gradient overlay */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.button>

                {/* Cancel Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="w-full px-6 py-3 text-gray-600 hover:text-gray-900 font-medium rounded-2xl hover:bg-gray-100 transition-colors"
                >
                  Stay Signed In
                </motion.button>
              </div>

              {/* Bottom decorative element */}
              <motion.div
                className="h-1 bg-gradient-to-r from-red-400 via-red-500 to-red-400"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  backgroundSize: '200% 100%',
                }}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LogoutModal;